import React from 'react';

import moment from 'moment';

import { connect, useSelector } from 'react-redux';
import t from 'react-translate';

// schemas
import { RootState } from 'redux/schemas';
import { NTimelineQuiz } from 'redux/schemas/models/quiz';
import { ActivityType } from 'redux/schemas/models/activity';
import { ComponentKeyPluralized } from 'redux/schemas/models/lecture-component';

// actions
import { setActivityExpandedInAutomatedCommunications } from 'redux/actions/course-communications';

// selectors
import { getQuiz, hasQuizGotCommunicationError } from 'redux/selectors/timeline';
import { isActivityExpandedInAutomatedCommunications } from 'redux/selectors/course-communications';

// components
import { NvTooltip } from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import NvStackedTab from 'shared/components/nv-stacked-tab';

import CommunicationCreateNewDropdown from 'course_communications/components/communication-create-new-dropdown';
import CommunicationItem from 'course_communications/components/communication-item/communication-item';

type QuizLectureComponentProps = {
  quizId: number
};

type StateProps = {
  quiz: NTimelineQuiz,
  hasCommunicationError: boolean,
};

const mapDispatchToProps = {
  setExpanded: setActivityExpandedInAutomatedCommunications,
};

const QuizLectureComponent = (props: QuizLectureComponentProps & StateProps & typeof mapDispatchToProps) => {
  const isExpanded = useSelector<RootState, boolean>((state) => isActivityExpandedInAutomatedCommunications(state, ComponentKeyPluralized.QUIZ, props.quizId));

  return (
    <React.Fragment>
      <div className={`lecture-component-row ${isExpanded && props.quiz?.communicationsCount > 0 && 'expanded-box'}`}>
        <NvIcon icon='quiz' size='smallest' />
        <div className='description'>{props.quiz?.title}</div>
        {props.quiz?.hasStructuralIssues && (
          <div className='text-danger spacing'>
            <NvIcon icon='warning' size='xss-smallest' />
          </div>
        )}
        {props.quiz?.hardDeadline && (
          <NvTooltip text={t.TIMELINE.HARD_DEADLINE()} placement='top'>
            <div className='spacing'>
              <NvIcon icon='harddeadline' size='smallest' />
            </div>
          </NvTooltip>
        )}
        <div className='date'>
          {props.quiz?.expirationDate && t.LECTURE_PAGES.COMPONENTS.DUE_DATE.STUDENT_VIEW(moment(props.quiz.expirationDate).format('LLL'))}
        </div>
        <div className=''>
          <CommunicationCreateNewDropdown activityType={ActivityType.QUIZ} activityId={props.quizId} />
        </div>
        { props.quiz?.communicationsCount > 0 && (
          <NvStackedTab
            count={props.quiz.communicationsCount}
            hasError={props.hasCommunicationError}
            isExpanded={isExpanded}
            errorMessage={t.COURSE_COMMUNICATIONS.WARNINGS.ACTIVITY_COMMUNICATION()}
            onClick={() => props.setExpanded({
              type: ComponentKeyPluralized.QUIZ,
              id: props.quizId,
              isExpanded: !isExpanded,
            })}
          />
        )}
      </div>
      {
        // Communications
        isExpanded && props.quiz?.communications && props.quiz.communications.map(communicationId => (
          <CommunicationItem key={communicationId.toString()} communicationId={communicationId} />
        ))
      }
    </React.Fragment>
  );
};

const ConnectedQuizLectureComponent = connect(
  (state: RootState, ownProps: QuizLectureComponentProps) => ({
    quiz: getQuiz(state, ownProps.quizId),
    hasCommunicationError: hasQuizGotCommunicationError(state, ownProps.quizId),
  }),
  mapDispatchToProps,
)(QuizLectureComponent);

export default ConnectedQuizLectureComponent;
